// src/components/ClientDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { IoArrowBack, IoPencil, IoKeypadOutline, IoMailOutline, IoCalendarOutline, IoScaleOutline, IoResizeOutline, IoMaleOutline, IoFemaleOutline, IoHelpCircleOutline } from 'react-icons/io5';
import ClientEditModal from './ClientEditModal';
import PasswordResetModal from './PasswordResetModal';
import AssignProgramModal from './AssignProgramModal';
import AssignNutritionModal from './AssignNutritionModal';
import ProgressChart from './ProgressChart';
import api from '../api';


const ClientDetail = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('assign'); // Para controlar la pestaña activa
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false); // Para el nuevo modal
  const [assignments, setAssignments] = useState([]);
  const [nutritionAssignments, setNutritionAssignments] = useState([]);
  const [isAssignNutritionModalOpen, setIsAssignNutritionModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [activeProgressMetric, setActiveProgressMetric] = useState('weight_kg');
  const [activityDays, setActivityDays] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);

    useEffect(() => {
  const fetchClientData = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const [detailsResponse, assignmentsResponse] = await Promise.all([
        axios.get(`http://127.0.0.1:8000/admin/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`http://127.0.0.1:8000/admin/clients/${clientId}/assignments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setClient(detailsResponse.data);
      setAssignments(assignmentsResponse.data);

      // Load nutrition assignments
      try {
        const nutRes = await api.get(`/admin/clients/${clientId}/nutrition-assignments`);
        setNutritionAssignments(nutRes.data);
      } catch (_) {}
    } catch (err) {
      setError('No se pudo cargar la información del cliente.');
    } finally {
      setLoading(false);
    }
  };
  fetchClientData();
}, [clientId]);

  useEffect(() => {
    if (activeTab === 'activity' && !activityLoaded) {
      setActivityLoading(true);
      api.get(`/admin/clients/${clientId}/workout-activity?limit_days=30`)
        .then(r => { setActivityDays(r.data); setActivityLoaded(true); })
        .catch(() => {})
        .finally(() => setActivityLoading(false));
    }
  }, [activeTab, clientId, activityLoaded]);

  useEffect(() => {
    if (activeTab === 'progress' && progressLogs.length === 0) {
      const fetchProgress = async () => {
        setProgressLoading(true);
        try {
          const response = await api.get(`/admin/clients/${clientId}/progress`);
          setProgressLogs(response.data);
        } catch (err) {
          console.error('Error cargando progreso del cliente', err);
        } finally {
          setProgressLoading(false);
        }
      };
      fetchProgress();
    }
  }, [activeTab, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

  const handleClientUpdated = (updatedClient) => {
    // Actualiza el estado del cliente con los nuevos datos recibidos del modal
    setClient(updatedClient);
  };
   const handleProgramAssigned = () => {
  const fetchAssignments = async () => {
    const token = localStorage.getItem('token');
    try {
        const response = await axios.get(`http://127.0.0.1:8000/admin/clients/${clientId}/assignments`, { headers: { Authorization: `Bearer ${token}` } });
        setAssignments(response.data);
    } catch (err) {
        console.error("Error recargando asignaciones", err);
    }
  };
  fetchAssignments();
};

  if (loading) return <p className="text-white text-center py-10">Cargando...</p>;
  if (error) return <p className="text-red-500 text-center py-10">{error}</p>;
  if (!client) return <p className="text-white text-center py-10">Cliente no encontrado.</p>;

  const statusConfig = {
    active:   { label: 'Activo',    classes: 'bg-green-500/20 text-green-400 border border-green-500/40' },
    inactive: { label: 'Inactivo',  classes: 'bg-red-500/20 text-red-400 border border-red-500/40' },
    pending:  { label: 'Pendiente', classes: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' },
  };
  const st = statusConfig[client.status] || statusConfig.inactive;

  const genderLabel = client.profile?.gender === 'Male' ? 'Masculino' : client.profile?.gender === 'Female' ? 'Femenino' : 'N/A';
  const GenderIcon = client.profile?.gender === 'Male' ? IoMaleOutline : client.profile?.gender === 'Female' ? IoFemaleOutline : IoHelpCircleOutline;
  
  // Usar los datos del coach devueltos por el backend
  const coachDisplay = client.assigned_coach_name && client.assigned_coach_email
    ? `${client.assigned_coach_name} (${client.assigned_coach_email})`
    : 'Sin coach';

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white">
        {isEditModalOpen && (
          <ClientEditModal
            client={client}
            onClose={() => setIsEditModalOpen(false)}
            onClientUpdated={handleClientUpdated}
          />
        )}
        {isResetModalOpen && (
          <PasswordResetModal
            client={client}
            onClose={() => setIsResetModalOpen(false)}
          />
        )}
        {isAssignModalOpen && (
          <AssignProgramModal
            client={client}
            onClose={() => setIsAssignModalOpen(false)}
            onProgramAssigned={handleProgramAssigned}
          />
        )}
        {isAssignNutritionModalOpen && (
          <AssignNutritionModal
            client={client}
            onClose={() => setIsAssignNutritionModalOpen(false)}
            onAssigned={() => {
              api.get(`/admin/clients/${clientId}/nutrition-assignments`)
                .then(r => setNutritionAssignments(r.data))
                .catch(() => {});
            }}
          />
        )}

      {/* Back link */}
      <Link to="/clients" className="inline-flex items-center text-gray-400 hover:text-white mb-6 gap-1">
        <IoArrowBack size={18} /> Clientes
      </Link>

      {/* Profile card */}
      <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-5 mb-6">
        {/* Avatar + name row */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 shrink-0 rounded-full bg-marizan-red flex items-center justify-center text-2xl font-bold text-white">
            {client.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white truncate">{client.name}</h1>
            <p className="text-gray-400 text-sm truncate flex items-center gap-1"><IoMailOutline size={13}/>{client.email}</p>
            <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${st.classes}`}>
              {st.label}
            </span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="bg-gray-700/50 rounded-xl p-3 flex flex-col gap-1">
            <span className="text-gray-400 text-xs flex items-center gap-1"><IoCalendarOutline size={12}/>Edad</span>
            <span className="text-white font-bold">{calculateAge(client.profile?.date_of_birth)} años</span>
          </div>
          <div className="bg-gray-700/50 rounded-xl p-3 flex flex-col gap-1">
            <span className="text-gray-400 text-xs flex items-center gap-1"><IoScaleOutline size={12}/>Peso</span>
            <span className="text-white font-bold">{client.profile?.weight_kg || 'N/A'} kg</span>
          </div>
          <div className="bg-gray-700/50 rounded-xl p-3 flex flex-col gap-1">
            <span className="text-gray-400 text-xs flex items-center gap-1"><IoResizeOutline size={12}/>Altura</span>
            <span className="text-white font-bold">{client.profile?.height_cm || 'N/A'} cm</span>
          </div>
          <div className="bg-gray-700/50 rounded-xl p-3 flex flex-col gap-1">
            <span className="text-gray-400 text-xs flex items-center gap-1"><GenderIcon size={12}/>Género</span>
            <span className="text-white font-bold">{genderLabel}</span>
          </div>
          <div className="bg-gray-700/50 rounded-xl p-3 flex flex-col gap-1">
            <span className="text-gray-400 text-xs flex items-center gap-1">Coach:</span>
            <span className="text-white font-bold">{coachDisplay}</span>
          </div>
        </div>



        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
          >
            <IoPencil size={16}/> Editar perfil
          </button>
          <button
            onClick={() => setIsResetModalOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
          >
            <IoKeypadOutline size={16}/> Contraseña
          </button>
        </div>
      </div>
<div className="mt-2">
  <div className="border-b border-gray-700 overflow-x-auto">
    <nav className="-mb-px flex min-w-max" aria-label="Tabs">
      <button
        onClick={() => setActiveTab('assign')}
        className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm focus:outline-none ${
          activeTab === 'assign'
          ? 'border-marizan-red text-marizan-red'
          : 'border-transparent text-gray-400 hover:text-gray-200'
        }`}
      >
        Entreno y Nutrición
      </button>
      <button
        onClick={() => setActiveTab('activity')}
        className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm focus:outline-none ${
          activeTab === 'activity'
          ? 'border-marizan-red text-marizan-red'
          : 'border-transparent text-gray-400 hover:text-gray-200'
        }`}
      >
        Actividad
      </button>
      <button
        onClick={() => setActiveTab('progress')}
        className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm focus:outline-none ${
          activeTab === 'progress'
          ? 'border-marizan-red text-marizan-red'
          : 'border-transparent text-gray-400 hover:text-gray-200'
        }`}
      >
        Progreso
      </button>
    </nav>
  </div>
  
  {/* Contenido que cambia según la pestaña activa */}
  <div className="py-8">
    {activeTab === 'assign' && (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-semibold text-white">Planes Asignados</h2>
      <button 
        onClick={() => setIsAssignModalOpen(true)} 
        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg"
      >
        Asignar Nuevo Programa
      </button>
    </div>
    
    {/* Lista de programas asignados */}
    <div className="space-y-4">
      {assignments.length > 0 ? (
        assignments.map(assign => (
          <div key={assign.id} className="bg-gray-800 p-4 rounded-lg flex justify-between items-center">
            <div>
              <p className="font-bold text-white">{assign.program.name}</p>
              <p className="text-sm text-gray-400">Fecha de Inicio: {new Date(assign.start_date).toLocaleDateString()}</p>
            </div>
            {/* En el futuro, aquí podría ir un botón para des-asignar */}
            <button className="text-red-500 hover:text-red-400 text-sm">Quitar</button>
          </div>
        ))
      ) : (
        <div className="text-center text-gray-400 border-2 border-dashed border-gray-600 p-8 rounded-lg">
          <p>Este cliente no tiene ningún programa asignado.</p>
        </div>
      )}
    </div>

    {/* ── Nutrición ── */}
    <div className="mt-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-white">Plan de Nutrición</h2>
        <button
          onClick={() => setIsAssignNutritionModalOpen(true)}
          className="bg-green-700 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg"
        >
          Asignar Plan Nutrición
        </button>
      </div>
      <div className="space-y-4">
        {nutritionAssignments.length > 0 ? (
          nutritionAssignments.map(assign => (
            <div key={assign.id} className="bg-gray-800 p-4 rounded-lg flex justify-between items-center">
              <div>
                <p className="font-bold text-white">{assign.plan?.name || '—'}</p>
                <p className="text-sm text-gray-400">Inicio: {new Date(assign.start_date).toLocaleDateString()}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 border-2 border-dashed border-gray-600 p-8 rounded-lg">
            <p>Este cliente no tiene ningún plan de nutrición asignado.</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}

    {activeTab === 'activity' && (
      <div>
        <h2 className="text-2xl font-semibold text-white mb-6">Actividad de {client.name}</h2>
        <p className="text-gray-400 text-sm mb-4">Últimos 30 días · ejercicios completados</p>

        {activityLoading && (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        )}

        {!activityLoading && activityDays.length === 0 && (
          <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-10">
            <p className="text-gray-400">Este cliente no ha registrado entrenamientos aún.</p>
          </div>
        )}

        {!activityLoading && activityDays.length > 0 && (
          <div className="space-y-4">
            {activityDays.map(day => (
              <div key={day.date} className="bg-black bg-opacity-40 rounded-xl border border-gray-700 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between bg-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">
                      {new Date(day.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                  <span className="text-xs bg-marizan-red/20 text-marizan-red rounded-full px-3 py-1 font-medium">
                    {day.total_exercises} ejercicio{day.total_exercises !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">{day.logs[0]?.routine_name}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {day.logs.map(log => (
                      <div key={log.id} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                        <div className="w-7 h-7 rounded-full bg-green-600/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{log.exercise_name}</p>
                          {(log.reps_completed || log.weight_lifted_lbs) && (
                            <p className="text-xs text-gray-400">
                              {log.reps_completed && `${log.reps_completed} reps`}
                              {log.reps_completed && log.weight_lifted_lbs && ' · '}
                              {log.weight_lifted_lbs && `${log.weight_lifted_lbs} lbs`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    {activeTab === 'progress' && (
      <div>
        <h2 className="text-2xl font-semibold text-white mb-6">Progreso de {client.name}</h2>

        {progressLoading && <p className="text-gray-400">Cargando progreso...</p>}

        {!progressLoading && progressLogs.length === 0 && (
          <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-10">
            <p className="text-gray-400">Este cliente no tiene registros de progreso todavía.</p>
          </div>
        )}

        {!progressLoading && progressLogs.length > 0 && (() => {
          const METRICS = [
            { key: 'weight_kg', label: 'Peso (kg)' },
            { key: 'body_fat_percentage', label: '% Grasa' },
            { key: 'waist_cm', label: 'Cintura (cm)' },
            { key: 'chest_cm', label: 'Pecho (cm)' },
            { key: 'hips_cm', label: 'Caderas (cm)' },
            { key: 'bicep_cm', label: 'Bícep (cm)' },
            { key: 'thigh_cm', label: 'Muslo (cm)' },
          ];
          const availableMetrics = METRICS.filter(m => progressLogs.some(l => l[m.key] != null));
          const currentMetric = availableMetrics.find(m => m.key === activeProgressMetric) || availableMetrics[0];

          return (
            <>
              {/* Gráfica */}
              <div className="bg-black bg-opacity-40 rounded-xl p-5 border border-gray-700 mb-6">
                <div className="flex flex-wrap gap-2 mb-4">
                  {availableMetrics.map(m => (
                    <button
                      key={m.key}
                      onClick={() => setActiveProgressMetric(m.key)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        activeProgressMetric === m.key
                          ? 'bg-marizan-red text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <ProgressChart
                  logs={progressLogs}
                  metric={currentMetric?.key || 'weight_kg'}
                  label={currentMetric?.label || 'Peso (kg)'}
                />
              </div>

              {/* Galería de fotos */}
              {progressLogs.some(l => l.photo_url) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Fotos de progreso</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {progressLogs.filter(l => l.photo_url).map(log => (
                      <div key={log.id} className="relative rounded-lg overflow-hidden aspect-square bg-gray-800">
                        <img src={log.photo_url} alt={`Progreso ${log.date}`} className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-xs text-center py-1 text-gray-300">
                          {new Date(log.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabla historial */}
              <div className="bg-black bg-opacity-40 rounded-xl border border-gray-700 overflow-hidden">
                <h3 className="text-lg font-semibold p-5 border-b border-gray-700">Historial</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700">
                        <th className="text-left px-5 py-3">Fecha</th>
                        <th className="text-right px-4 py-3">Peso (kg)</th>
                        <th className="text-right px-4 py-3">% Grasa</th>
                        <th className="text-right px-4 py-3">Cintura</th>
                        <th className="text-left px-4 py-3">Notas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...progressLogs].reverse().map(log => (
                        <tr key={log.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                          <td className="px-5 py-3 font-medium text-white">
                            {new Date(log.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300">{log.weight_kg ?? '—'}</td>
                          <td className="px-4 py-3 text-right text-gray-300">{log.body_fat_percentage != null ? `${log.body_fat_percentage}%` : '—'}</td>
                          <td className="px-4 py-3 text-right text-gray-300">{log.waist_cm ?? '—'}</td>
                          <td className="px-4 py-3 text-gray-400 max-w-xs truncate">{log.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    )}
  </div>
</div>
    </div>
  );
};

export default ClientDetail;